import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { situationBadgeOptions } from '@/common/components/SituationBadge';
import { formatCurrency } from '@/common/utils/formatCurrency';

import { getInfiniteBatchDebitNotePrivate } from '../services/getInfiniteBatchDebitNotePrivate';
import { getInfiniteBatchDebitNotePublic } from '../services/getInfiniteBatchDebitNotePublic';
import { IBatchDebitNotePaginated } from '../services/getInfiniteBatchDebitNotePublic';

import { useRecoverTaxes } from './useRecoverTaxes';
import { useTranslateDebitNoteStatus } from './useTranslateDebitNoteStatus';

export function useInfiniteBatchDebitNote(
  batchId?: string,
  search = '',
  usePrivateRoute = false,
) {
  const [fetchedData, setFetchedData] = useState<IBatchDebitNotePaginated[]>(
    [],
  );
  const { t } = useTranslation(['common', 'invoices/invoices']);
  const { recoverTaxes } = useRecoverTaxes();
  const { translateDebitNoteStatus } = useTranslateDebitNoteStatus();

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['infiniteBatchDebitNote', batchId],
    ({ pageParam }) =>
      usePrivateRoute
        ? getInfiniteBatchDebitNotePrivate(batchId!, pageParam)
        : getInfiniteBatchDebitNotePublic(batchId!, pageParam),
    {
      enabled: !!batchId,
      getNextPageParam: lastPage => {
        const nextPage = lastPage.data.meta.currentPage + 1;

        return nextPage <= lastPage.data.meta.totalPages ? nextPage : undefined;
      },
      getPreviousPageParam: firstPage => {
        const previousPage = firstPage.data.meta.currentPage - 1;

        return previousPage >= 1 ? previousPage : undefined;
      },
    },
  );

  const filterSearchedData = useCallback(
    (fetched: IBatchDebitNotePaginated[]) => {
      const searchLowerCase = search.toLowerCase();

      return fetched.filter(item => {
        const taxes = recoverTaxes(item.DebitNote);
        const contractCode = item.DebitNote.Sign.Deal.dealCode;
        const statusLabel = t(
          situationBadgeOptions.find(opt => opt.id === item.DebitNote.status)
            ?.label || item.status,
          { ns: 'common' },
        );
        const isSingleInvoiceLabel = item.DebitNote.isSingleInvoice
          ? t('invoices/invoices:singleInvoice')
          : '';

        const propertiesToCompare = [
          item.DebitNote.numberDebitNote,
          format(new Date(item.DebitNote.issueDate), 'd/MM/yyyy'),
          format(new Date(item.DebitNote.dueDate), 'd/MM/yyyy'),
          item.DebitNote?.description,
          item.DebitNote?.Sign?.Deal?.Plan?.title,
          item.DebitNote?.debitType,
          item.DebitNote.Customer.name,
          item.DebitNote.Customer.email,
          item.DebitNote.Customer.documentNumber,
          item.DebitNote.Customer.externalCode,
          contractCode,
          statusLabel,
          isSingleInvoiceLabel,
          formatCurrency(item.DebitNote.totalAmount),
          formatCurrency(item.DebitNote.netAmount),
          translateDebitNoteStatus(item.DebitNote.status),
        ];

        if (taxes?.cofinsAmount) {
          propertiesToCompare.push(formatCurrency(taxes.cofinsAmount));
        }

        if (taxes?.pisAmount) {
          propertiesToCompare.push(formatCurrency(taxes.pisAmount));
        }

        return propertiesToCompare.some(property =>
          property?.toLowerCase().includes(searchLowerCase),
        );
      });
    },
    [search, recoverTaxes, translateDebitNoteStatus],
  );

  useEffect(() => {
    if (data && search) {
      setFetchedData(prev =>
        filterSearchedData(
          [...prev, ...data.pages.flatMap(page => page.data.items)].filter(
            (register, index, self) =>
              index === self.findIndex(c => c.id === register.id),
          ),
        ),
      );
    }
  }, [data, filterSearchedData, search]);

  return {
    batchDebitNotes:
      search && search?.length
        ? fetchedData
        : data?.pages.flatMap(page => page.data.items),
    isLoadingBatchDebitNotes: isLoading,
    hasMoreDebitNotes: hasNextPage,
    hasPreviousDebitNotes: hasPreviousPage,
    isFetchingDebitNotes: isFetching,
    fetchMoreDebitNotes: fetchNextPage,
  };
}
